<template>
  <div ref="scrollContainer" class="embed-ai-search">
    <header class="result-header">
      <div class="brand">
        <img class="brand-mark" src="@/assets/images/login/u69.png" alt="" aria-hidden="true" />
        <div>
          <h1>{{ pageTitle }}</h1>
          <p>{{ statusText }}</p>
        </div>
      </div>
      <button v-if="isStreaming" class="stop-button" type="button" @click="stopCurrentRequest">
        停止生成
      </button>
    </header>

    <main class="result-main">
      <section v-if="showWaitingState" class="state-panel" role="status">
        <div class="signal" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <h2>{{ stateTitle }}</h2>
        <p>{{ stateDescription }}</p>
      </section>

      <article v-if="answer" class="answer-panel">
        <div class="markdown-content" v-html="renderedAnswer"></div>
        <div v-if="isStreaming" class="stream-cursor" aria-label="正在生成"></div>
      </article>
    </main>
  </div>
</template>

<script>
import { EmbedRequestError, sendEmbedMessage } from "@/api/chatbot";
import { createAbortController } from "@/utils/abortController";
import { renderEmbedMarkdown } from "@/utils/embedMarkdown";
import { consumeEventStream, StreamParseError } from "@/utils/embedStream";
import getPageTitle from "@/utils/get-page-title";

const PROTOCOL_VERSION = 1;
const QUERY_MESSAGE_TYPE = "ai-search:query";
const READY_MESSAGE_TYPE = "ai-search:ready";
const STATUS_MESSAGE_TYPE = "ai-search:status";
const WAIT_TIMEOUT = 10 * 1000;
const FIRST_DATA_TIMEOUT = 60 * 1000;
const NETWORK_ERROR_MESSAGE = "网络连接异常，请稍后重新查询";

function getAllowedOrigins() {
  const developmentOrigins =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500,http://127.0.0.1:5500"
      : "";

  return (process.env.VUE_APP_EMBED_ALLOWED_ORIGINS || developmentOrigins)
    .split(",")
    .map((origin) => origin.trim().replace(/\/$/, ""))
    .filter(Boolean);
}

function getBusinessErrorMessage(code) {
  const errorCode = Number(code);
  if (errorCode === 108) {
    return "当前账号暂无使用权限";
  }
  if (errorCode === 109) {
    return "验证凭证无效或已过期";
  }
  if (errorCode === 104) {
    return "检索参数不正确";
  }
  return "AI 服务暂时无法完成本次检索";
}

function getHostWindows() {
  const hosts = [];
  let current = window;
  while (current.parent && current.parent !== current) {
    current = current.parent;
    hosts.push(current);
    if (current === window.top) {
      break;
    }
  }
  return hosts;
}

function isTrustedHost(source) {
  return getHostWindows().some((host) => source === host);
}

function postToHostWindows(message, targetOrigin) {
  getHostWindows().forEach((host) => {
    try {
      host.postMessage(message, targetOrigin);
    } catch (e) {
      // targetOrigin 与接收窗口 origin 不匹配时静默跳过
    }
  });
}

export default {
  name: "AiSearchEmbed",
  data() {
    return {
      allowedOrigins: getAllowedOrigins(),
      phase: "waiting",
      answer: "",
      errorMessage: "",
      activeRequestId: "",
      parentOrigin: "",
      controller: null,
      waitTimer: null,
      firstDataTimer: null,
      requestTimedOut: false,
      searchType: 2,
    };
  },
  computed: {
    pageTitle() {
      return Number(this.searchType) === 1 ? "AI推荐结果" : "AI检索结果";
    },
    isStreaming() {
      return this.phase === "thinking" || this.phase === "streaming";
    },
    renderedAnswer() {
      return renderEmbedMarkdown(this.answer);
    },
    showWaitingState() {
      return !this.answer;
    },
    statusText() {
      const labels = {
        waiting: "等待宿主页面发送检索参数",
        thinking: "正在分析问题",
        streaming: "正在生成回答",
        complete: "回答由电晓晓AI生成",
        cancelled: "已停止生成",
        error: "检索失败",
      };
      return labels[this.phase] || labels.waiting;
    },
    stateTitle() {
      if (this.phase === "thinking") {
        return "思考中...";
      }
      if (this.phase === "cancelled") {
        return "生成已停止";
      }
      if (this.phase === "error") {
        return "暂时无法检索";
      }
      return "等待检索";
    },
    stateDescription() {
      if (this.phase === "thinking") {
        return "正在连接 AI 服务并整理相关信息";
      }
      if (this.phase === "cancelled") {
        return "宿主页面可以重新发送查询";
      }
      if (this.phase === "error") {
        return this.errorMessage;
      }
      return this.errorMessage || "页面就绪后将自动接收宿主页面发送的查询";
    },
  },
  watch: {
    pageTitle: {
      immediate: true,
      handler(title) {
        document.title = getPageTitle(title);
      },
    },
  },
  created() {
    const querySearchType = Number(this.$route.query.search_type);
    if (Number.isInteger(querySearchType)) {
      this.searchType = querySearchType;
    }
  },
  mounted() {
    document.body.classList.add("ai-search-embed-body");
    window.addEventListener("message", this.handleParentMessage);
    this.startWaitingTimer();
    this.announceReady();
  },
  beforeDestroy() {
    document.body.classList.remove("ai-search-embed-body");
    window.removeEventListener("message", this.handleParentMessage);
    this.clearWaitingTimer();
    this.clearFirstDataTimer();
    this.abortRequest();
  },
  methods: {
    startWaitingTimer() {
      this.clearWaitingTimer();
      this.waitTimer = window.setTimeout(() => {
        if (this.phase === "waiting") {
          this.errorMessage = "尚未收到检索参数，请在电小二重新打开或发送查询";
        }
      }, WAIT_TIMEOUT);
    },
    clearWaitingTimer() {
      if (this.waitTimer) {
        window.clearTimeout(this.waitTimer);
        this.waitTimer = null;
      }
    },
    startFirstDataTimer(requestId, controller) {
      this.clearFirstDataTimer();
      this.requestTimedOut = false;
      this.firstDataTimer = window.setTimeout(() => {
        if (!this.answer && this.isActiveRequest(requestId, controller)) {
          this.requestTimedOut = true;
          controller.abort();
        }
      }, FIRST_DATA_TIMEOUT);
    },
    clearFirstDataTimer() {
      if (this.firstDataTimer) {
        window.clearTimeout(this.firstDataTimer);
        this.firstDataTimer = null;
      }
    },
    announceReady() {
      if (!getHostWindows().length) {
        this.phase = "error";
        this.errorMessage = "请通过已授权的宿主页面打开 AI 检索结果";
        return;
      }
      if (!this.allowedOrigins.length) {
        this.phase = "error";
        this.errorMessage = "嵌入页尚未配置允许访问的宿主域名";
        return;
      }

      let targetOrigins = this.allowedOrigins;
      if (document.referrer) {
        try {
          const referrerOrigin = new URL(document.referrer).origin;
          if (this.allowedOrigins.includes(referrerOrigin)) {
            targetOrigins = [referrerOrigin];
          }
        } catch (error) {
          targetOrigins = this.allowedOrigins;
        }
      }

      const readyMessage = {
        type: READY_MESSAGE_TYPE,
        version: PROTOCOL_VERSION,
      };
      targetOrigins.forEach((origin) => {
        postToHostWindows(readyMessage, origin);
      });
    },
    handleParentMessage(event) {
      if (!isTrustedHost(event.source) || !this.allowedOrigins.includes(event.origin)) {
        return;
      }

      const validation = this.validateQueryMessage(event.data);
      if (!validation.matchesProtocol) {
        return;
      }

      this.parentOrigin = event.origin;
      if (validation.error) {
        this.phase = "error";
        this.errorMessage = validation.error;
        this.postStatus("error", event.data && event.data.request_id, "INVALID_PARAMETERS");
        return;
      }

      const query = validation.query;
      if (query.request_id === this.activeRequestId && this.isStreaming) {
        return;
      }

      this.searchType = query.search_type;
      this.clearWaitingTimer();
      this.runQuery(query);
    },
    validateQueryMessage(message) {
      console.log("接收到的消息:", message);
      if (!message || typeof message !== "object" || message.type !== QUERY_MESSAGE_TYPE) {
        return { matchesProtocol: false };
      }
      if (message.version !== PROTOCOL_VERSION) {
        return { matchesProtocol: true, error: "不支持的通信协议版本" };
      }
      if (typeof message.request_id !== "string") {
        return { matchesProtocol: true, error: "request_id 必须是字符串类型" };
      }
      if (!message.request_id.trim()) {
        return { matchesProtocol: true, error: "request_id 不能为空" };
      }
      if (message.request_id.length > 32) {
        return { matchesProtocol: true, error: "request_id 长度不能超过 32 个字符" };
      }
      if (message.send_message == null) {
        return { matchesProtocol: true, error: "缺少检索内容 send_message" };
      }
      if (typeof message.token !== "string") {
        return { matchesProtocol: true, error: "token 必须是字符串类型" };
      }
      if (!message.token.trim()) {
        return { matchesProtocol: true, error: "token 不能为空" };
      }
      if (message.search_type == null) {
        return { matchesProtocol: true, error: "缺少 search_type 参数" };
      }
      if (!Number.isInteger(message.search_type)) {
        return { matchesProtocol: true, error: "search_type 必须是整数" };
      }
      if (typeof message.openid !== "string") {
        return { matchesProtocol: true, error: "openid 必须是字符串类型" };
      }
      if (!message.openid.trim()) {
        return { matchesProtocol: true, error: "openid 不能为空" };
      }
      if (typeof message.openKey !== "string") {
        return { matchesProtocol: true, error: "openKey 必须是字符串类型" };
      }
      if (!message.openKey.trim()) {
        return { matchesProtocol: true, error: "openKey 不能为空" };
      }

      // 必传参数已校验，其余参数原样透传给接口
      const { type: _type, version: _version, ...rest } = message;
      const query = {
        ...rest,
        request_id: message.request_id.trim(),
        token: message.token.trim(),
        openid: message.openid.trim(),
        openKey: message.openKey.trim(),
      };

      return {
        matchesProtocol: true,
        query,
      };
    },
    async runQuery(query) {
      this.abortRequest();

      const requestId = query.request_id;
      let controller = null;

      try {
        controller = createAbortController();
        this.activeRequestId = requestId;
        this.controller = controller;
        this.answer = "";
        this.errorMessage = "";
        this.phase = "thinking";
        this.postStatus("thinking", requestId);
        this.startFirstDataTimer(requestId, controller);

        // request_id 仅用于内部追踪，不传给接口；其余参数全部透传
        const { request_id: _rid, ...apiPayload } = query;

        const response = await sendEmbedMessage(apiPayload, controller.signal);
        const contentType = (response.headers.get("Content-Type") || "").toLowerCase();

        if (contentType.includes("application/json")) {
          const payload = await response.json();
          this.consumePayload(payload, requestId, controller, { useWikiContent: true });
        } else {
          await consumeEventStream(response, (payload) => {
            this.consumePayload(payload, requestId, controller);
          });
        }

        if (!this.isActiveRequest(requestId, controller)) {
          return;
        }
        if (!this.answer) {
          throw new StreamParseError("AI 服务未返回可展示的内容");
        }

        this.phase = "complete";
        this.postStatus("complete", requestId);
      } catch (error) {
        if (controller && !this.isActiveRequest(requestId, controller)) {
          return;
        }
        if (this.requestTimedOut && !this.answer) {
          this.phase = "error";
          this.errorMessage = NETWORK_ERROR_MESSAGE;
          this.postStatus("error", requestId, "REQUEST_TIMEOUT");
          return;
        }
        if (error && error.name === "AbortError") {
          this.phase = "cancelled";
          this.postStatus("cancelled", requestId);
          return;
        }

        this.phase = "error";
        this.errorMessage = this.getRequestErrorMessage(error);
        this.postStatus("error", requestId, "REQUEST_FAILED");
      } finally {
        this.clearFirstDataTimer();
        if (controller && this.controller === controller) {
          this.controller = null;
        }
      }
    },
    consumePayload(payload, requestId, controller, options = {}) {
      if (!this.isActiveRequest(requestId, controller)) {
        return;
      }
      if (!payload || typeof payload !== "object") {
        throw new StreamParseError("AI 返回数据格式不正确");
      }
      if (payload.retcode !== undefined && Number(payload.retcode) !== 200) {
        const error = new Error(getBusinessErrorMessage(payload.retcode));
        error.name = "BusinessError";
        error.code = payload.retcode;
        throw error;
      }

      const data = payload.data || {};
      const answer = typeof data.answer === "string" ? data.answer : "";
      const wikiContent = typeof data.content === "string" ? data.content : "";
      const content = options.useWikiContent ? wikiContent || answer : answer || wikiContent;

      if (content) {
        this.clearFirstDataTimer();
        this.answer = options.useWikiContent ? content : this.answer + content;
        this.phase = "streaming";
        this.postStatus("streaming", requestId);
        this.scrollToBottom();
      }
    },
    getRequestErrorMessage(error) {
      if (error && error.name === "BusinessError") {
        return error.message;
      }
      if (error instanceof EmbedRequestError) {
        if (error.status === 401 || error.status === 403) {
          return "验证凭证无效或已过期";
        }
        return "AI 服务暂时不可用，请稍后重新查询";
      }
      if (error instanceof StreamParseError) {
        return error.message;
      }
      return NETWORK_ERROR_MESSAGE;
    },
    isActiveRequest(requestId, controller) {
      return this.activeRequestId === requestId && this.controller === controller;
    },
    stopCurrentRequest() {
      const requestId = this.activeRequestId;
      this.abortRequest();
      this.phase = "cancelled";
      this.postStatus("cancelled", requestId);
    },
    abortRequest() {
      this.clearFirstDataTimer();
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    },
    postStatus(state, requestId, code) {
      if (!this.parentOrigin || !getHostWindows().length) {
        return;
      }

      const message = {
        type: STATUS_MESSAGE_TYPE,
        version: PROTOCOL_VERSION,
        request_id: requestId || "",
        state,
      };
      if (code) {
        message.code = code;
      }
      postToHostWindows(message, this.parentOrigin);
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
  },
};
</script>

<style>
body.ai-search-embed-body {
  overflow: hidden;
}
</style>

<style scoped lang="scss">
.embed-ai-search {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  color: #20242b;
  background: #fff;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
}

.embed-ai-search::after {
  content: "";
  flex: 0 0 30px;
  width: 100%;
  height: 30px;
}

.result-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 12px 22px;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #dfe2dc;
}

.brand {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;

  h1 {
    margin: 0;
    color: #171a1f;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  p {
    margin: 3px 0 0;
    overflow: hidden;
    color: #72776f;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.brand-mark {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.stop-button {
  flex: 0 0 auto;
  height: 32px;
  padding: 0 14px;
  color: #b94a1e;
  font-size: 13px;
  background: #fff8f4;
  border: 1px solid #e7a17f;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #fff0e8;
  }
}

.result-main {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding: 0;
}

.state-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 20px;
  text-align: center;
  background: #fff;

  h2 {
    margin: 20px 0 8px;
    color: #252a2f;
    font-size: 19px;
  }

  p {
    max-width: 520px;
    margin: 0;
    color: #777d75;
    font-size: 13px;
    line-height: 1.8;
  }
}

.signal {
  display: flex;
  height: 28px;
  align-items: flex-end;
  gap: 5px;

  span {
    width: 4px;
    height: 10px;
    background: #ee6b2f;
    animation: signal-pulse 1s ease-in-out infinite;

    &:nth-child(2) {
      height: 20px;
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      height: 15px;
      animation-delay: 0.3s;
    }
  }
}

.answer-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding: 24px;
  background: #fff;
}

.answer-label {
  margin-bottom: 18px;
  color: #a34a22;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.markdown-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  color: #252a2f;
  font-size: 12px;
  line-height: 1.8;

  ::v-deep {
    h1,
    h2,
    h3,
    h4 {
      margin: 1.3em 0 0.6em;
      line-height: 1.4;
    }

    h1 {
      font-size: 22px;
    }

    h2 {
      font-size: 19px;
    }

    h3,
    h4 {
      font-size: 16px;
    }

    p {
      margin: 0 0 1em;
    }

    a {
      color: #b64f20;
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    pre {
      max-width: 100%;
      padding: 14px;
      overflow-x: auto;
      background: #f1f2ef;
      border: 1px solid #dde0da;
    }

    code {
      font-family: Consolas, "Courier New", monospace;
    }

    .table-scroll {
      width: 100%;
      max-width: 100%;
      margin: 16px 0;
      overflow-x: auto;
      border: 1px solid #d7dad4;
    }

    table {
      width: max-content;
      min-width: 100%;
      border-collapse: collapse;
      background: #fff;
    }

    th,
    td {
      min-width: 120px;
      padding: 10px 12px;
      text-align: left;
      white-space: normal;
      border-right: 1px solid #dfe2dc;
      border-bottom: 1px solid #dfe2dc;
    }

    th {
      color: #373c42;
      font-weight: 700;
      background: #f0f1ed;
    }
  }
}

.stream-cursor {
  width: 8px;
  height: 18px;
  margin-top: 6px;
  background: #ee6b2f;
  animation: cursor-blink 0.9s steps(2, start) infinite;
}

@keyframes signal-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scaleY(0.6);
  }
  50% {
    opacity: 1;
    transform: scaleY(1);
  }
}

@keyframes cursor-blink {
  50% {
    opacity: 0;
  }
}

@media (max-width: 600px) {
  .result-header {
    min-height: 60px;
    padding: 10px 14px;
  }

  .answer-panel {
    padding: 18px 14px;
  }

  .brand p {
    max-width: 210px;
  }
}
</style>
